import {
  analytics as analyticsApi,
  profit as profitApi,
  type Analytics as AnalyticsData,
  type Profit as ProfitData,
} from '../lib/api';
import { Link } from 'react-router-dom';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Copyable, Spinner, Stat } from '../components/ui';

const BASESCAN_ADDR = 'https://basescan.org/address/';

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

/** Parse a formatted NGN string ("1,234.56") back to a number for sign/coloring. */
function ngnNum(s: string): number {
  return Number(s.replace(/,/g, ''));
}

function ProfitSection({ p }: { p: ProfitData }) {
  const total = ngnNum(p.total_profit_ngn);
  const positive = total >= 0;
  const color = positive ? 'var(--success, #3ecf8e)' : 'var(--danger, #f2555a)';
  const usdTotal = ngnNum(p.total_profit_usd);
  const usdPositive = usdTotal >= 0;
  const usdColor = usdPositive ? 'var(--success, #3ecf8e)' : 'var(--danger, #f2555a)';
  const hasStripe = p.onramp_stripe.orders > 0 || usdTotal !== 0;

  return (
    <Card title="Profit">
      {!p.cost_rate_set && (
        <Alert tone="warning">
          No cost basis set — assuming ₦{p.cost_rate}/USDC. Set your real USDC cost on the{' '}
          <Link to="/rates">Rates</Link> page for an accurate figure.
        </Alert>
      )}
      <p className="muted" style={{ marginTop: 0 }}>
        The spread you've earned against your USDC cost basis (₦{p.cost_rate}/USDC), across completed
        orders only. Onramps profit when you sell USDC above cost; offramps when you buy it below.
        {hasStripe && ' Stripe (USD) card onramps are shown separately in USD (spread over $1 parity).'}
      </p>

      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', margin: '4px 0 20px' }}>
        <div>
          <div className="muted" style={{ fontSize: 13 }}>Realised profit (NGN rails)</div>
          <div className="mono" style={{ fontSize: 34, fontWeight: 600, color }}>
            {positive ? '' : '−'}₦{p.total_profit_ngn.replace(/^-/, '')}
          </div>
        </div>
        {hasStripe && (
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Realised profit (Stripe USD)</div>
            <div className="mono" style={{ fontSize: 34, fontWeight: 600, color: usdColor }}>
              {usdPositive ? '' : '−'}${p.total_profit_usd.replace(/^-/, '')}
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 16,
        }}
      >
        {hasStripe && (
          <div>
            <h4 style={{ margin: '0 0 8px' }}>Stripe onramps · {p.onramp_stripe.orders.toLocaleString()} completed</h4>
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
              <dt className="muted">USD charged</dt>
              <dd className="mono" style={{ margin: 0 }}>${p.onramp_stripe.usd_in}</dd>
              <dt className="muted">USDC sold, at $1 parity</dt>
              <dd className="mono" style={{ margin: 0 }}>${p.onramp_stripe.value_usd}</dd>
              <dt className="muted">Profit</dt>
              <dd className="mono" style={{ margin: 0, color: ngnNum(p.onramp_stripe.profit_usd) >= 0 ? usdColor : 'var(--danger, #f2555a)' }}>
                ${p.onramp_stripe.profit_usd}
              </dd>
            </dl>
          </div>
        )}
        <div>
          <h4 style={{ margin: '0 0 8px' }}>Onramps · {p.onramp.orders.toLocaleString()} completed</h4>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
            <dt className="muted">NGN received</dt>
            <dd className="mono" style={{ margin: 0 }}>₦{p.onramp.ngn_in}</dd>
            <dt className="muted">USDC sold, at cost</dt>
            <dd className="mono" style={{ margin: 0 }}>₦{p.onramp.cost_ngn}</dd>
            <dt className="muted">Profit</dt>
            <dd className="mono" style={{ margin: 0, color: ngnNum(p.onramp.profit_ngn) >= 0 ? color : 'var(--danger, #f2555a)' }}>
              ₦{p.onramp.profit_ngn}
            </dd>
          </dl>
        </div>
        <div>
          <h4 style={{ margin: '0 0 8px' }}>Offramps · {p.offramp.orders.toLocaleString()} completed</h4>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', margin: 0 }}>
            <dt className="muted">USDC received, at cost</dt>
            <dd className="mono" style={{ margin: 0 }}>₦{p.offramp.value_ngn}</dd>
            <dt className="muted">NGN paid out</dt>
            <dd className="mono" style={{ margin: 0 }}>₦{p.offramp.ngn_out}</dd>
            <dt className="muted">Profit</dt>
            <dd className="mono" style={{ margin: 0, color: ngnNum(p.offramp.profit_ngn) >= 0 ? color : 'var(--danger, #f2555a)' }}>
              ₦{p.offramp.profit_ngn}
            </dd>
          </dl>
        </div>
      </div>
    </Card>
  );
}

function ageOf(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h`;
  return `${Math.floor(mins / 1440)}d`;
}

// Lightweight inline bar chart (no chart lib). Two series per day.
function VolumeChart({ daily }: { daily: AnalyticsData['daily'] }) {
  const rows = daily.map((d) => ({
    date: d.date,
    on: Number(d.onramp_ngn.replace(/,/g, '')),
    off: Number(d.offramp_ngn.replace(/,/g, '')),
  }));
  const max = Math.max(1, ...rows.map((r) => Math.max(r.on, r.off)));

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160, paddingTop: 8 }}>
      {rows.map((r) => (
        <div
          key={r.date}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
          title={`${r.date}\nOnramp ₦${r.on.toLocaleString()}\nOfframp ₦${r.off.toLocaleString()}`}
        >
          <div
            style={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-end',
              height: 130,
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 8,
                height: `${(r.on / max) * 100}%`,
                background: 'var(--accent, #6c8cff)',
                borderRadius: 2,
                minHeight: r.on > 0 ? 2 : 0,
              }}
            />
            <div
              style={{
                width: 8,
                height: `${(r.off / max) * 100}%`,
                background: 'var(--success, #3ecf8e)',
                borderRadius: 2,
                minHeight: r.off > 0 ? 2 : 0,
              }}
            />
          </div>
          <span className="muted" style={{ fontSize: 10 }}>
            {r.date.slice(5)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Analytics() {
  const res = useResource(() => analyticsApi.get(), [], { refreshMs: 30000 });
  const profitRes = useResource(() => profitApi.get(), [], { refreshMs: 30000 });
  const a = res.data;

  return (
    <>
      <div className="page-header">
        <h1>Analytics</h1>
        <p>Transaction volumes, success rates, and payments that need attention.</p>
      </div>

      {res.error && <Alert>{res.error}</Alert>}
      {!a ? (
        <Spinner />
      ) : (
        <>
          {/* Headline */}
          <div className="stat-grid" style={{ marginBottom: 16 }}>
            <Stat label="Completed transactions" value={a.totals.completed.toLocaleString()} />
            <Stat label="Overall success rate" value={pct(a.totals.success_rate)} />
            <Stat label="In progress" value={a.totals.pending.toLocaleString()} />
            <Stat
              label="Stuck (needs attention)"
              value={a.stuck.onramp_count + a.stuck.offramp_count}
            />
          </div>

          {/* Profit — spread earned against the USDC cost basis */}
          {profitRes.data && (
            <div style={{ marginBottom: 16 }}>
              <ProfitSection p={profitRes.data} />
            </div>
          )}

          {/* Stuck payments — the money-at-risk section */}
          {a.stuck.orders.length > 0 && (
            <Card
              title={`⚠ Stuck payments — ₦${a.stuck.ngn_at_risk} + ${a.stuck.usdc_at_risk} USDC at risk`}
            >
              <p className="muted" style={{ marginTop: 0 }}>
                One leg settled but the other did not: the user sent funds and has not received the
                other side. Review and resolve these.
              </p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Type</th>
                      <th>Received</th>
                      <th>Destination</th>
                      <th>Status</th>
                      <th>Age</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.stuck.orders.map((o) => (
                      <tr key={o.id}>
                        <td>
                          <Copyable value={o.id} display={o.id.slice(0, 10)} />
                        </td>
                        <td>{o.kind}</td>
                        <td className="mono">{o.received}</td>
                        <td>
                          {o.kind === 'onramp' ? (
                            <Copyable
                              value={o.destination}
                              display={`${o.destination.slice(0, 8)}…${o.destination.slice(-4)}`}
                              href={BASESCAN_ADDR + o.destination}
                            />
                          ) : (
                            <Copyable value={o.destination} display={o.destination} />
                          )}
                        </td>
                        <td>
                          <Badge tone={o.status === 'failed' ? 'danger' : 'warning'}>
                            {o.status}
                          </Badge>
                        </td>
                        <td className="muted">{ageOf(o.created_at)}</td>
                        <td className="muted">{formatDateTime(o.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Per-flow breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 16 }}>
            <Card title="Onramps (NGN → USDC)">
              <div className="stat-grid">
                <Stat label="Completed" value={a.onramp.completed.toLocaleString()} />
                <Stat label="Success rate" value={pct(a.onramp.success_rate)} />
                <Stat label="NGN in" value={`₦${a.onramp.ngn_in}`} />
                <Stat label="USDC out" value={a.onramp.usdc_out} />
                <Stat label="Pending" value={a.onramp.pending} />
                <Stat label="Failed" value={a.onramp.failed} />
              </div>
            </Card>
            <Card title="Offramps (USDC → NGN)">
              <div className="stat-grid">
                <Stat label="Completed" value={a.offramp.completed.toLocaleString()} />
                <Stat label="Success rate" value={pct(a.offramp.success_rate)} />
                <Stat label="USDC in" value={a.offramp.usdc_in} />
                <Stat label="NGN out" value={`₦${a.offramp.ngn_out}`} />
                <Stat label="Pending" value={a.offramp.pending} />
                <Stat label="Failed" value={a.offramp.failed} />
              </div>
            </Card>
            {(a.onramp_stripe.completed > 0 ||
              a.onramp_stripe.pending > 0 ||
              a.onramp_stripe.held > 0 ||
              a.onramp_stripe.failed > 0) && (
              <Card title="Stripe onramps (USD card → USDC)">
                <div className="stat-grid">
                  <Stat label="Completed" value={a.onramp_stripe.completed.toLocaleString()} />
                  <Stat label="Success rate" value={pct(a.onramp_stripe.success_rate)} />
                  <Stat label="USD in" value={`$${a.onramp_stripe.usd_in}`} />
                  <Stat label="USDC out" value={a.onramp_stripe.usdc_out} />
                  <Stat label="Held (settling)" value={a.onramp_stripe.held} />
                  <Stat label="Failed" value={a.onramp_stripe.failed} />
                </div>
              </Card>
            )}
          </div>

          {/* Fees */}
          <Card title="Fees paid" >
            <div className="stat-grid">
              <Stat label="Payout fees (NGN)" value={`₦${a.fees.payout_fee_ngn}`} />
              <Stat label="Dextopus cut (USDC)" value={a.fees.dextopus_cut_usdc} />
            </div>
          </Card>

          {/* Daily volume */}
          <Card title="Daily volume (last 14 days)">
            <div style={{ display: 'flex', gap: 16, marginBottom: 4 }}>
              <span className="muted" style={{ fontSize: 12 }}>
                <span style={{ color: 'var(--accent, #6c8cff)' }}>■</span> Onramp NGN
              </span>
              <span className="muted" style={{ fontSize: 12 }}>
                <span style={{ color: 'var(--success, #3ecf8e)' }}>■</span> Offramp NGN
              </span>
            </div>
            <VolumeChart daily={a.daily} />
          </Card>
        </>
      )}
    </>
  );
}
